import { Fieldset } from "@/components/forms/Fieldset"
import { Button } from "@/components/ui/button"
import { FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { MinusIcon, PlusIcon, TrashIcon } from "lucide-react"
import { useFieldArray } from "react-hook-form"
import type { UseFormReturn } from "react-hook-form"

type StringListFieldProps = {
  form: UseFormReturn<any>
  name: string
  label: string
  description?: string
  addButtonLabel: string
  removeButtonLabel?: string
  removeButtonAriaLabel?: string
  showRemoveLabel?: boolean
  inputPlaceholder?: string
  newItemValue?: string
}

export const StringListField = ({
  form,
  name,
  label,
  description,
  addButtonLabel,
  removeButtonLabel,
  removeButtonAriaLabel,
  showRemoveLabel = false,
  inputPlaceholder,
  newItemValue = ""
}: StringListFieldProps) => {
  const { control } = form
  const { fields, append, remove } = useFieldArray({
    control,
    name: name as never
  })

  const handleAdd = () => append((newItemValue ?? "") as never)

  return (
    <Fieldset label={label} description={description}>
      <div className="space-y-3">
        {fields.map((field, index) => (
          <div key={field.id} className="flex items-start gap-3">
            <FormField
              control={control}
              name={`${name}.${index}` as never}
              render={({ field: stringField }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <Input {...stringField} placeholder={inputPlaceholder} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="flex items-center gap-1.5"
              onClick={() => remove(index)}
              aria-label={removeButtonAriaLabel ?? removeButtonLabel ?? "Remove item"}
            >
              <TrashIcon className="size-4" />
              {showRemoveLabel && removeButtonLabel ? <span>{removeButtonLabel}</span> : null}
            </Button>
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="flex items-center gap-1.5"
          onClick={handleAdd}
        >
          <PlusIcon className="size-4" />
          {addButtonLabel}
        </Button>
      </div>
    </Fieldset>
  )
}
